import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Flex, Grid, Text, IconButton, Spinner, Icon, Button, RadioGroup, Radio, Stack, Checkbox,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp, FiAlertTriangle, FiRotateCcw } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  getAiRuns, getAiRun, revertRun, RUN_KIND, RUN_ALERTS, SITTING_STATUS, reasonLabel, formatScore,
} from 'api/administrador/monitoring/aiGrading';
import { PeriodPicker, ChipGroup, StatusPill, TabIntro } from './common';
import ConfirmActionModal from './ConfirmActionModal';
import AiPagination from './AiPagination';

const PAGE_SIZE = 20;
const COLS = '1.05fr 1.2fr 1fr 2.2fr 0.8fr 0.9fr 48px';
const n = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const KIND_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'grade', label: 'Calificación' },
  { value: 'release', label: 'Publicación' },
  { value: 'problems', label: 'Con problemas' },
  { value: 'revert', label: 'Reversiones' },
  { value: 'action', label: 'Acciones manuales' },
];

function startedLabel(date) {
  const d = dayjs(date);
  if (d.isSame(dayjs(), 'day')) return `Hoy ${d.format('HH:mm')}`;
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return `Ayer ${d.format('HH:mm')}`;
  return d.format('DD MMM · HH:mm');
}

function duration(ms) {
  if (ms === null || ms === undefined) return 'en curso';
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`;
}

function ResultBar({ run }) {
  const c = run.counts;
  if (run.kind === 'action' || run.kind === 'revert') {
    const count = run.kind === 'revert' ? c.reverted : c.items;
    return (
      <Text fontSize="13px" color="gray.600">
        {run.kind === 'revert' ? n(count, 'entrega revertida', 'entregas revertidas') : `${n(count, 'entrega', 'entregas')} · acción manual`}
      </Text>
    );
  }
  if (run.kind === 'release') {
    return (
      <Box>
        <Box h="8px" borderRadius="999px" bg="#10B981" />
        <Text fontSize="12px" color="gray.600" mt={1.5}>{n(c.released, 'publicada', 'publicadas')}{c.errors ? ` · ${n(c.errors, 'con error', 'con error')}` : ''}</Text>
      </Box>
    );
  }
  const total = c.items || 1;
  const pct = (v) => `${(v / total) * 100}%`;
  const parts = [
    n(c.graded, 'calificada', 'calificadas'),
    c.deferred ? `${c.deferred} al docente` : null,
    c.retries ? `${c.retries} en reintento` : null,
    c.errors ? `${c.errors} con error` : null,
  ].filter(Boolean);
  return (
    <Box>
      <Flex h="8px" borderRadius="999px" overflow="hidden" bg="gray.100">
        <Box w={pct(c.graded)} bg="#10B981" />
        <Box w={pct(c.deferred)} bg="#F59E0B" />
        <Box w={pct(c.errors + c.retries)} bg="#EF4444" />
      </Flex>
      <Text fontSize="12px" color="gray.600" mt={1.5}>{parts.join(' · ')}</Text>
    </Box>
  );
}

function RevertModal({ run, detail, isOpen, onClose, onDone }) {
  const [mode, setMode] = useState('requeue');
  const [pauseSubjects, setPauseSubjects] = useState(run.alerts.length > 0);
  const published = detail.published;
  return (
    <ConfirmActionModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Revertir ejecución #${run.id}`}
      description="Se descartan las notas que el estudiante aún no ve. Nadie recibe notificación."
      confirmLabel={`Revertir ${detail.unpublished} ${detail.unpublished === 1 ? 'entrega' : 'entregas'}`}
      danger
      successMessage={(r) => `Ejecución revertida: ${r.reverted} ${r.reverted === 1 ? 'entrega' : 'entregas'}${r.pausedSubjectIds?.length ? ` · ${r.pausedSubjectIds.length} en pausa` : ''}`}
      onConfirm={(reason) => revertRun(run.id, { mode, pauseSubjects, reason })}
      onDone={onDone}
    >
      <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={2.5} mb={4}>
        <Box p={3} borderRadius="10px" bg="#EFF6FF" border="1px solid #BFDBFE">
          <Text fontSize="22px" fontWeight="700" color="#1E3A8A">{detail.unpublished}</Text>
          <Text fontSize="13px" color="#1E40AF">sin publicar · se revierten</Text>
        </Box>
        <Box p={3} borderRadius="10px" bg="#F1F5F9" border="1px solid #E2E8F0">
          <Text fontSize="22px" fontWeight="700" color="#334155">{published}</Text>
          <Text fontSize="13px" color="#475569">ya publicadas · no se tocan</Text>
        </Box>
      </Grid>
      <Text fontSize="14px" fontWeight="600" mb={2}>¿Qué pasa con las entregas sin publicar?</Text>
      <RadioGroup value={mode} onChange={setMode}>
        <Stack spacing={2}>
          <Radio value="requeue" colorScheme="purple">
            <Text fontSize="14px" fontWeight="600">Volver a la cola</Text>
            <Text fontSize="13px" color="gray.600">Se califican otra vez en la siguiente ejecución. Úsalo después de corregir la causa.</Text>
          </Radio>
          <Radio value="teacher" colorScheme="purple">
            <Text fontSize="14px" fontWeight="600">Pasar al docente</Text>
            <Text fontSize="13px" color="gray.600">Aparecen de inmediato en su bandeja para calificación manual.</Text>
          </Radio>
        </Stack>
      </RadioGroup>
      <Checkbox mt={4} isChecked={pauseSubjects} onChange={(e) => setPauseSubjects(e.target.checked)} colorScheme="purple">
        <Text fontSize="14px">Pausar {detail.subjects === 1 ? 'la materia' : `las ${detail.subjects} materias`} de la ejecución</Text>
      </Checkbox>
    </ConfirmActionModal>
  );
}

function RunDetail({ run, onOpenSitting, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    getAiRun(run.id).then(setDetail).catch(() => setDetail({ error: true }));
  }, [run.id, reload]);
  const canRevert = (run.kind === 'grade' || run.kind === 'test') && detail && !detail.error && detail.unpublished > 0;
  const flagged = run.alerts.length > 0;
  return (
    <Box px={5} py={4} bg={flagged ? '#FFFBEB' : 'gray.50'} borderTop="1px dashed" borderColor={flagged ? '#FDE68A' : 'gray.200'}>
      {flagged && (
        <Flex gap={3} align="flex-start" p={3} mb={4} borderRadius="10px" bg="white" border="1px solid" borderColor="#FDE68A">
          <Icon as={FiAlertTriangle} color="#B45309" mt="2px" />
          <Box fontSize="13px">
            <Text fontWeight="600" color="#92400E">Ejecución fuera de lo normal</Text>
            {run.alerts.map((a) => (
              <Text key={a.code} color="#78350F">{(RUN_ALERTS[a.code] || (() => a.code))(a)}</Text>
            ))}
          </Box>
        </Flex>
      )}
      {!detail && <Flex justify="center" py={4}><Spinner color="#574feb" size="sm" /></Flex>}
      {detail?.error && <Text fontSize="sm" color="red.600">No se pudo cargar la ejecución.</Text>}
      {detail && !detail.error && (
        <>
          <Grid templateColumns="repeat(4, minmax(0, 1fr))" gap={2.5} mb={4}>
            {[
              ['Entregas', detail.items.length],
              ['Sin publicar', detail.unpublished],
              ['Ya publicadas', detail.published],
              ['Materias', detail.subjects],
            ].map(([label, value]) => (
              <Box key={label} bg="white" border="1px solid" borderColor="gray.200" borderRadius="10px" p={3}>
                <Text fontSize="11px" color="gray.500" fontWeight="600" textTransform="uppercase">{label}</Text>
                <Text fontSize="22px" fontWeight="700">{value}</Text>
              </Box>
            ))}
          </Grid>
          {detail.stats?.reverted && (
            <Text fontSize="13px" color="gray.600" mb={3}>
              Revertida en la ejecución #{detail.stats.reverted.by}: {detail.stats.reverted.count} {detail.stats.reverted.count === 1 ? 'entrega' : 'entregas'}
              {detail.stats.reverted.mode === 'teacher' ? ' al docente' : ' a la cola'}.
            </Text>
          )}
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="10px" overflow="hidden">
            {detail.items.map((it) => {
              const st = it.sitting ? SITTING_STATUS[it.sitting.status] : null;
              return (
                <Grid key={`${it.sittingId}-${it.outcome}`} templateColumns="2.4fr 0.6fr 1.6fr 1.2fr" px={4} py={2.5} alignItems="center" borderTop="1px solid" borderColor="gray.100" _first={{ borderTop: 0 }}>
                  <Box minW={0}>
                    <Button
                      variant="link" color="gray.800" fontSize="sm" fontWeight="600" isTruncated maxW="100%" justifyContent="flex-start"
                      onClick={() => onOpenSitting(it.sittingId)}
                    >
                      {it.sitting?.activity?.title || `Entrega #${it.sittingId}`}
                    </Button>
                    <Text fontSize="xs" color="gray.500" isTruncated>
                      {[it.sitting?.student?.name, it.sitting?.subject?.name, it.sitting?.program?.name].filter(Boolean).join(' · ')}
                    </Text>
                  </Box>
                  <Text fontSize="sm" fontWeight="700">{it.outcome === 'graded' || it.outcome === 'released' ? formatScore(it.score) : '—'}</Text>
                  <Text fontSize="xs" color="gray.600">
                    {it.outcome === 'graded' ? 'Calificada' : it.outcome === 'released' ? 'Publicada' : it.outcome === 'retry' ? 'Reintento'
                      : it.outcome === 'deferred' ? `Al docente · ${reasonLabel(it.reason) || ''}` : `Error · ${reasonLabel(it.reason) || ''}`}
                  </Text>
                  <Box justifySelf="start">{st && <StatusPill label={`Hoy: ${st.label}`} tone={st.tone} />}</Box>
                </Grid>
              );
            })}
          </Box>
          {canRevert && (
            <Flex justify="flex-end" mt={4}>
              <Button
                leftIcon={<FiRotateCcw />}
                onClick={() => setReverting(true)}
                bg="#B91C1C"
                color="white"
                _hover={{ bg: '#991B1B' }}
                borderRadius="10px"
              >
                Revertir ejecución
              </Button>
            </Flex>
          )}
          {canRevert && (
            <RevertModal
              run={run}
              detail={detail}
              isOpen={reverting}
              onClose={() => setReverting(false)}
              onDone={() => {
                setReload((n) => n + 1);
                if (onChanged) onChanged();
              }}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default function RunsTab({ tick, onOpenSitting, onChanged, queue }) {
  const [filters, setFilters] = useState({ period: 'today', kind: '', page: 1 });
  const [runs, setRuns] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const res = await getAiRuns({
        period: filters.period,
        kind: filters.kind === 'problems' ? '' : filters.kind,
        problems: filters.kind === 'problems' ? 'true' : '',
        page: filters.page,
        pageSize: PAGE_SIZE,
      });
      setRuns(res?.data || []);
      setPagination(res?.pagination || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[calificación] error cargando ejecuciones:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => { if (tick) load(true); }, [tick]);

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
      <TabIntro title="Historial">
        Cada vez que el sistema califica (cada 10 minutos) o publica (cada hora de 7:00 a 21:00) queda una ejecución en
        este historial. Si una ejecución se ve fuera de lo normal, ábrela y revierte lo que aún no se ha publicado.
      </TabIntro>
      <Flex px={5} py={4} gap={4} justify="space-between" align="center" wrap="wrap">
        <Flex gap={4} align="center" wrap="wrap">
          <PeriodPicker label={false} value={filters.period} onChange={(period) => setFilters({ ...filters, period, page: 1 })} />
          <ChipGroup ariaLabel="Tipo de ejecución" options={KIND_OPTIONS} value={filters.kind} onChange={(kind) => setFilters({ ...filters, kind, page: 1 })} />
        </Flex>
        <Box textAlign="right">
          {queue && (
            <Text fontSize="12px" color={queue.alertRecipients ? 'gray.500' : '#B45309'}>
              {queue.alertRecipients
                ? `Las ejecuciones fuera de lo normal se avisan por correo a ${queue.alertRecipients} ${queue.alertRecipients === 1 ? 'persona' : 'personas'}`
                : 'Sin alertas por correo (configura AI_ALERT_EMAILS en el API)'}
            </Text>
          )}
        </Box>
      </Flex>

      <Grid templateColumns={COLS} px={5} py={3} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase">
        <Text>Ejecución</Text><Text>Tipo</Text><Text>Inicio</Text><Text>Resultado</Text><Text>Nota prom.</Text><Text>Duración</Text><Text />
      </Grid>

      {loading && runs.length === 0 && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}
      {!loading && runs.length === 0 && (
        <Text textAlign="center" py={10} color="gray.500" fontSize="sm">No hay ejecuciones en el periodo.</Text>
      )}

      {runs.map((r) => {
        const kind = RUN_KIND[r.kind] || RUN_KIND.action;
        const isOpen = open === r.id;
        const flagged = r.alerts.length > 0;
        return (
          <Box key={r.id}>
            <Grid templateColumns={COLS} px={5} py={3.5} alignItems="center" borderTop="1px solid" borderColor="gray.100" bg={flagged ? '#FFFBEB' : 'white'}>
              <Box>
                <Text fontSize="sm" fontWeight="600">#{r.id}</Text>
                {r.revertedBy && <Text fontSize="11px" color="#B91C1C" fontWeight="600">revertida</Text>}
              </Box>
              <Flex align="center" gap={2} fontSize="sm"><Box w="8px" h="8px" borderRadius="999px" bg={kind.dot} />{kind.label}</Flex>
              <Text fontSize="sm">{startedLabel(r.startedAt)}</Text>
              <ResultBar run={r} />
              <Text fontSize="sm" fontWeight="600" color={r.alerts.some((a) => a.code === 'AVG_OFF') ? '#B45309' : 'gray.800'}>
                {r.kind === 'grade' ? formatScore(r.avgScore) : '—'}
              </Text>
              <Text fontSize="sm">{duration(r.durationMs)}</Text>
              <IconButton
                aria-label={isOpen ? `Contraer ejecución ${r.id}` : `Ver ejecución ${r.id}`}
                aria-expanded={isOpen}
                icon={isOpen ? <FiChevronUp /> : <FiChevronDown />}
                size="sm" variant="outline" borderRadius="8px" bg="white"
                onClick={() => setOpen(isOpen ? null : r.id)}
              />
            </Grid>
            {isOpen && <RunDetail run={r} onOpenSitting={onOpenSitting} onChanged={onChanged} />}
          </Box>
        );
      })}

      <AiPagination pagination={pagination} noun="ejecuciones" onPageChange={(page) => setFilters({ ...filters, page })} />
    </Box>
  );
}
